import {Component, ElementRef, HostListener, Input, OnChanges, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {FormModel} from "../form/form.model.abstract";
import {UntypedFormGroup} from "@angular/forms";
import {FormModelRoofpitch} from "../form/form.model.roofpitch";

/**
 * Component with al slider and a roof for the input of the pitch
 */
@Component({
    selector: '[app-formfield-roofpitch]',
    templateUrl: './formfield.roofpitch.component.html',
    standalone: false
})
export class FormfieldRoofpitchComponent implements OnInit, OnChanges {
  /**
   * Formmodel of the value
   */
  @Input() formModel: FormModel;

  /**
   * form
   */
  @Input() form: UntypedFormGroup;

  /**
   * current step
   */
  @Input() step: string;

  /**
   * The fieldtype. "pitch" vs. pent.
   * @see FormModelRoofpitch.TYPE_*
   */
  @Input() fieldType: string;

  /**
   * Sliderbar
   */
  @ViewChild('sliderbar') sliderbar: ElementRef;

  /**
   * The active parte of the sliderbar
   */
  @ViewChild('sliderbarActive') sliderbarActive: ElementRef;

  /**
   * sliderthumb
   */
  @ViewChild('sliderthumb') sliderthumb: ElementRef;

  /**
   * toggle that is true while dragging
   */
  drag: boolean = false;

  /**
   * minimum of the slider.
   */
  min = 30;

  /**
   * maximum of the slider
   */
  max = 60;

  /**
   * current slider value.
   */
  sliderValue: number;

  /**
   * percentage of movement between min and max
   */
  sliderPercentage: number;

  /**
   * The y-position of the roof pitch, depending on the roof type.
   */
  roofPitchPosition = 50;

  /**
   * interval for small animation of the roof putch
   */
  interval: any;

  /**
   * @inheritdoc
   */
  ngOnInit() {
    this.sliderValue = this.form.get(this.formModel.id).value ?? this.formModel.getValue();

    this.updatePercentage(this.sliderValue);
    this.updateRoofPitchPosition();
  }

  /**
   * @inheritdoc
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges) {
    // catch changes on the fieldtype
    if(changes.fieldType?.isFirstChange() === false) {
      this.fieldType = changes.fieldType.currentValue;
      this.updateRoofPitchPosition(true);
    }
  }

  /**
   * change the roof top position on th ex axis, based on the roof type
   * @param animated
   */
  updateRoofPitchPosition(animated: boolean = false): void {
    clearInterval(this.interval);
    const newRoofPitchPosition = this.fieldType === FormModelRoofpitch.TYPE_PITCH ? 50 : 0;
    if(animated === false) {
      this.roofPitchPosition = newRoofPitchPosition;
      return;
    }

    if(newRoofPitchPosition !== this.roofPitchPosition) {
      this.interval = setInterval(() => {
        if(newRoofPitchPosition === this.roofPitchPosition) {
          clearInterval(this.interval);
        }
        else {
          this.roofPitchPosition = newRoofPitchPosition > this.roofPitchPosition ? this.roofPitchPosition + 1 : this.roofPitchPosition - 1;
        }
      }, 1);
    }
  }

  /**
   * set the value to the model when the slider is moved.
   * @param value
   */
  setValue(value: number) {
    this.form.get(this.formModel.id).setValue(value);
  }

  /**
   * start dragging
   * @param event
   */
  dragStart(event: Event): void {
    this.drag = true;
  }

  /**
   * stop dragging
   * @param event
   */
  @HostListener("window:mouseup", ["$event"])
  @HostListener("window:touchend", ["$event"])
  dragEnd(event: Event): void {
    this.drag = false;
  }

  /**
   * prevent scrolling while dragging
   * @param event
   */
  onDrag(event: Event): void {
    if(this.drag) {
      event.preventDefault();
    }
  }

  /**
   * Handling the drag event of the thumb
   * @param event
   */
  @HostListener('document:mousemove', ['$event'])
  @HostListener('document:touchmove', ['$event'])
  onMouseMove(event: any) {
    if (this.drag === false) {
      // Only continue if mouse button is down
      return;
    }

    // avoid scrolling on mobile devices
    const touch = event.touches ? event.touches[0] : event;
    const y = touch.clientY;

    const sliderbar = this.sliderbar.nativeElement.getBoundingClientRect();
    const max = sliderbar.height;

    const newTop = y - sliderbar.top;

    if(newTop > 0 && newTop < max) {
      this.sliderPercentage = 100 - Math.round(newTop / max * 100);
      this.sliderValue = Math.round(this.min + ((this.max - this.min) * (this.sliderPercentage / 100)));

      this.setValue(this.sliderValue);
    }
  }

  /**
   * Update the slider percentage after move and after init.
   * @param value
   */
  updatePercentage(value: number): void {
    this.sliderPercentage = (value - this.min) / (this.max - this.min) * 100;
  }

  /**
   * Handle arrow up key press to increase the slider value
   * @param event Keyboard event
   */
  onArrowUp(event: Event): void {
    // Prevent default scrolling behavior
    event.preventDefault();

    if (this.sliderValue < this.max) {
      this.sliderValue = Math.min(this.max, this.sliderValue + 1);
      this.updatePercentage(this.sliderValue);
      this.setValue(this.sliderValue);
    }
  }

  /**
   * Handle arrow down key press to decrease the slider value
   * @param event Keyboard event
   */
  onArrowDown(event: Event): void {
    // Prevent default scrolling behavior
    event.preventDefault();

    if (this.sliderValue > this.min) {
      this.sliderValue = Math.max(this.min, this.sliderValue - 1);
      this.updatePercentage(this.sliderValue);
      this.setValue(this.sliderValue);
    }
  }
}
